/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { litellmRotateInstanceKey } from "../funcs/litellmRotateInstanceKey.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { LitellmInstanceKeyResult } from "../models/components/litellminstancekeyresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RotateLiteLLMInstanceKeyRequest,
  RotateLiteLLMInstanceKeySecurity,
} from "../models/operations/rotatelitellminstancekey.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RotateLiteLLMInstanceKeyMutationVariables = {
  request: RotateLiteLLMInstanceKeyRequest;
  security?: RotateLiteLLMInstanceKeySecurity | undefined;
  options?: RequestOptions;
};

export type RotateLiteLLMInstanceKeyMutationData = LitellmInstanceKeyResult;

export type RotateLiteLLMInstanceKeyMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * rotateInstanceKey litellm
 *
 * @remarks
 * Atomically replace a LiteLLM integration key and return the new plaintext value once. Requires org:admin.
 */
export function useRotateLiteLLMInstanceKeyMutation(
  options?: MutationHookOptions<
    RotateLiteLLMInstanceKeyMutationData,
    RotateLiteLLMInstanceKeyMutationError,
    RotateLiteLLMInstanceKeyMutationVariables
  >,
): UseMutationResult<
  RotateLiteLLMInstanceKeyMutationData,
  RotateLiteLLMInstanceKeyMutationError,
  RotateLiteLLMInstanceKeyMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRotateLiteLLMInstanceKeyMutation(client, options),
    ...options,
  });
}

export function mutationKeyRotateLiteLLMInstanceKey(): MutationKey {
  return ["@gram/client", "litellm", "rotateInstanceKey"];
}

export function buildRotateLiteLLMInstanceKeyMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RotateLiteLLMInstanceKeyMutationVariables,
  ) => Promise<RotateLiteLLMInstanceKeyMutationData>;
} {
  return {
    mutationKey: mutationKeyRotateLiteLLMInstanceKey(),
    mutationFn: function rotateLiteLLMInstanceKeyMutationFn({
      request,
      security,
      options,
    }): Promise<RotateLiteLLMInstanceKeyMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(litellmRotateInstanceKey(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
